"use client"

import { useEffect, useLayoutEffect, useRef } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"

import { useHomeActivation } from "@/home"
import { useOrganization } from "@clerk/nextjs"
import { cn, Loader } from "@coval/ui"
import { useMediaQuery } from "usehooks-ts"

import { useErrorFallbackShown } from "@/lib/appErrorFallback"
import { isKeyboardTypingTarget } from "@/lib/isKeyboardTypingTarget"
import { buildScopedStorageKey } from "@/utils/scopedStorageKey"

import { useCovi } from "../contexts/CoviContext"
import { useCoviHighlightTarget } from "../hooks/useCoviHighlightTarget"
import { useCoviViewObserver } from "../hooks/useCoviViewObserver"
import { useSofiaPageSummaryPresenter } from "../hooks/useSofiaPageSummaryPresenter"
import { useSofiaPanelContent } from "../hooks/useSofiaPanelContent"
import { PANEL_STACKING_CLASS } from "../model/constants"
import { isSofiaPanelAvailable } from "../utils/routes"
import { getSofiaHomeThreadId, isSofiaSurfacePath } from "../utils/sofiaHome"

// A failed panel import almost always means this tab is still on a deployment
// whose chunks are no longer served. Re-running the import in the same page
// fails again, so the only recovery is a page reload onto the current
// deployment. The button says so, because the reload also resets the page
// beside Sofia; pages with unsaved edits still get their beforeunload prompt
// (useUnsavedChanges) before anything is discarded.
export function CoviPanelLoadState({ error }: { error?: Error | null }) {
  return (
    <div
      role={error ? "alert" : undefined}
      className={cn(
        "fixed right-4 top-4 flex items-center gap-2 rounded-lg border border-coval-border bg-surface-default px-3 py-2 text-sm font-medium text-coval-text shadow-lg",
        PANEL_STACKING_CLASS,
      )}
    >
      {error ? (
        <>
          <span>Sofia couldn&apos;t load.</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border border-coval-border bg-surface-1 px-2 py-1 text-xs font-medium text-coval-text transition-colors hover:bg-surface-2"
          >
            Reload page
          </button>
        </>
      ) : (
        <>
          <Loader size="sm" label="Loading Sofia" />
          <span>Loading Sofia…</span>
        </>
      )}
    </div>
  )
}

const CoviPanel = dynamic(
  () => import("./CoviPanel").then((module) => module.CoviPanel),
  {
    ssr: false,
    loading: CoviPanelLoadState,
  },
)

// Scoped per organization (below) so the nudge fires once per new org, not
// once per browser ever.
const AUTO_OPEN_STORAGE_KEY_BASE = "coval:copilot:onboarding-opened"
const AUTO_OPEN_ROUTES = ["/getting-started", "/dashboard", "/[slug]/dashboard"]
const MOBILE_QUERY = "(max-width: 768px)"
const NO_STREAMING: ReadonlySet<string> = new Set()

function pathMatchesRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`)
}

/**
 * Mounts the persistent copilot for authenticated users. On desktop the panel
 * docks on the right and the page content reflows to make room; on mobile it
 * falls back to a floating overlay.
 * Proactively opens in onboarding mode the first time a brand-new organization
 * lands on an eligible legacy route. Sofia Home owns its onboarding surface,
 * so the panel stays closed there. Opening and closing are driven by the app
 * shell header affordance plus the ⌘/Ctrl-I shortcut.
 */
export function CoviMount() {
  const panelContent = useSofiaPanelContent()
  const {
    isOpen,
    open,
    toggle,
    width,
    isResizing,
    threads,
    activeThreadId,
    setActiveThread,
    showThreadOnHome,
    returnHomeThreadToPanel,
    streamingThreadIds = NO_STREAMING,
    draftThreadIds = NO_STREAMING,
  } = useCovi()
  const router = useRouter()
  const autoOpenedRef = useRef(false)
  const { organization } = useOrganization()
  const autoOpenStorageKey = buildScopedStorageKey(
    AUTO_OPEN_STORAGE_KEY_BASE,
    "organization",
    { organizationId: organization?.id },
  )
  const isMobile = useMediaQuery(MOBILE_QUERY)
  // An error boundary below the shell has replaced the page with its fallback.
  // Sofia is mounted above that boundary and would otherwise stay on screen
  // beside the error, offering a chat about a page that no longer renders.
  // Treat it as a route that grants no Sofia surface at all: every entry point
  // below reads these three values, so clearing them here closes the panel, the
  // header shortcut, and the full-page surface together — while
  // `shouldKeepBackgroundRuntime` still preserves an in-flight turn or an
  // unsent draft in the hidden background variant.
  const errorFallbackShown = useErrorFallbackShown()
  const sofiaPanelAvailable =
    !errorFallbackShown &&
    isSofiaPanelAvailable({
      pathname: router.pathname,
      asPath: router.asPath,
    })
  const homeThreadId = errorFallbackShown
    ? null
    : getSofiaHomeThreadId(router.asPath)
  const fullPage = Boolean(homeThreadId)
  // Match Home on the VISIBLE path, not the Next route pattern: a workspace URL
  // (/org/workspace/<id>/home) is served by the same page component but never
  // matches "/[slug]/home", and treating it as a non-Home route lets the docked
  // panel paint beside the landing — two Sofia surfaces at once.
  const onSofiaSurfaceRoute =
    !errorFallbackShown && isSofiaSurfacePath(router.asPath)
  // A streamed turn owns its fetch from the mounted thread view, and an unsent
  // draft lives only in that view's composer — a transcript rehydrates from
  // storage, a draft does not. Closing the panel or navigating away from Home
  // must not unmount either.
  const shouldKeepBackgroundRuntime =
    (panelContent || streamingThreadIds.size > 0 || draftThreadIds.size > 0) &&
    (!isOpen || !sofiaPanelAvailable) &&
    !onSofiaSurfaceRoute
  const autoOpenEligible = AUTO_OPEN_ROUTES.some((route) =>
    pathMatchesRoute(router.pathname, route),
  )
  const needsAutoOpenCounts =
    !isOpen && sofiaPanelAvailable && autoOpenEligible && !fullPage
  const needsConfigCounts =
    needsAutoOpenCounts || fullPage || (isOpen && sofiaPanelAvailable)
  const { data: counts } = useHomeActivation({
    enabled: needsConfigCounts,
  })
  // The Sofia Home landing owns the visible briefing surface, so the docked
  // panel never shows there — even if the panel was left open elsewhere.
  const docked = isOpen && sofiaPanelAvailable && !isMobile && !fullPage
  const appliedHomeThreadRef = useRef<string | null>(null)

  // Warm the lazily-imported CoviPanel chunk while the user sits on the Home
  // landing, before they submit. Otherwise the first message pays for the
  // chunk fetch mid-transition and flashes the "Loading Sofia…" state; loading
  // it ahead of time makes the switch to the full-page chat feel instant.
  useEffect(() => {
    if (onSofiaSurfaceRoute) {
      void import("./CoviPanel")
    }
  }, [onSofiaSurfaceRoute])

  // A Home thread URL is a first-class entry point. Keep the panel runtime
  // logically open so navigating from full-page chat to a product page changes
  // only its presentation; the active stream and composer stay mounted.
  useEffect(() => {
    if (homeThreadId) {
      open("toggle")
    }
  }, [homeThreadId, open])

  // Direct links and browser history select the URL's thread once. Local
  // actions update selection before they update the URL, so key this effect to
  // the URL id rather than activeThreadId to avoid fighting those transitions.
  useEffect(() => {
    if (!homeThreadId) {
      appliedHomeThreadRef.current = null
      return
    }
    if (
      appliedHomeThreadRef.current === homeThreadId &&
      threads.some((thread) => thread.id === homeThreadId)
    ) {
      return
    }
    if (threads.some((thread) => thread.id === homeThreadId)) {
      appliedHomeThreadRef.current = homeThreadId
      setActiveThread(homeThreadId)
      return
    }
    if (threads.length > 0 && activeThreadId) {
      appliedHomeThreadRef.current = activeThreadId
      showThreadOnHome(activeThreadId)
    }
  }, [activeThreadId, homeThreadId, setActiveThread, showThreadOnHome, threads])

  // Transient highlighting: glow-then-fade a resource Sofia just created/changed when it
  // auto-navigates you to it. Reads the ?covi_highlight= param on arrival and
  // clears it — a no-op on every normal page load. Mounted here (app-shell
  // level, survives navigation) so it fires wherever Sofia lands you.
  useCoviHighlightTarget()

  // View context: keep the view-context registry observing what the user is looking
  // at (declared primaries, on-screen rows, focus stack) so every chat turn can
  // snapshot it at send time. Mounted here for the same reasons as the
  // highlight hook: app-shell level, survives navigation, flag-gated.
  useCoviViewObserver()

  // A page summary is one of Sofia's messages, so publishing one shows it in
  // the chat — but only while the chat is open; it never opens the panel
  // itself. The same route policy that controls every panel entry point also
  // controls whether a page may publish into it.
  useSofiaPageSummaryPresenter({
    enabled: sofiaPanelAvailable && !panelContent,
  })

  // CoviMount is mounted at the app-shell level, above page layouts, so it
  // survives an organization switch. autoOpenedRef must reset when the org
  // (and therefore autoOpenStorageKey) changes — otherwise having already
  // auto-opened for one new organization this session permanently blocks the
  // nudge for the next new organization switched to, even though that
  // organization's own key was never set.
  const autoOpenStorageKeyRef = useRef(autoOpenStorageKey)
  if (autoOpenStorageKeyRef.current !== autoOpenStorageKey) {
    autoOpenStorageKeyRef.current = autoOpenStorageKey
    autoOpenedRef.current = false
  }

  useEffect(() => {
    if (autoOpenedRef.current || isOpen || !counts || !sofiaPanelAvailable) {
      return
    }
    if (typeof window === "undefined" || !autoOpenStorageKey) {
      return
    }
    const isNewOrg = (counts.agent_count ?? 0) === 0
    if (!isNewOrg || !autoOpenEligible) {
      return
    }
    if (window.localStorage.getItem(autoOpenStorageKey)) {
      return
    }
    autoOpenedRef.current = true
    window.localStorage.setItem(autoOpenStorageKey, "1")
    open("auto_open")
  }, [
    autoOpenEligible,
    autoOpenStorageKey,
    sofiaPanelAvailable,
    counts,
    isOpen,
    open,
    router.pathname,
  ])

  // ⌘/Ctrl-I follows the same route policy as every other panel entry point.
  useEffect(() => {
    if (!sofiaPanelAvailable) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.shiftKey ||
        isKeyboardTypingTarget(event.target)
      ) {
        return
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "i") {
        event.preventDefault()
        if (fullPage) {
          returnHomeThreadToPanel()
        } else {
          toggle()
        }
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [fullPage, returnHomeThreadToPanel, sofiaPanelAvailable, toggle])

  // The full-page Sofia surface replaces only the app shell's content region,
  // leaving global navigation in place. Measure that region instead of
  // hard-coding sidebar widths, which vary with collapse and workspace nav.
  useLayoutEffect(() => {
    const root = document.documentElement
    if (!fullPage) {
      root.style.removeProperty("--covi-home-left")
      return
    }
    const main = document.querySelector<HTMLElement>(
      "[data-app-shell-main-region]",
    )
    if (!main) {
      root.style.setProperty("--covi-home-left", "0px")
      return
    }
    const update = () => {
      root.style.setProperty(
        "--covi-home-left",
        `${Math.round(main.getBoundingClientRect().left)}px`,
      )
    }
    update()
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update)
    observer?.observe(main)
    window.addEventListener("resize", update)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", update)
      root.style.removeProperty("--covi-home-left")
    }
  }, [fullPage])

  // Reflow the app shell's content for the docked panel via CSS variables the
  // shell reads — this keeps the shell decoupled from the copilot module (no
  // import, no shared context). Cleared when closed or on mobile so the content
  // reclaims the full width and the panel overlays instead.
  //
  // Opening Sofia should snap the content into place, not slide it — clicking
  // "Ask Sofia" opens the panel with no reflow animation. Docking straight out
  // of the full-page surface is a presentation swap on the same live thread and
  // likewise snaps. Only genuine width changes while already docked keep the
  // 200ms ease, and an in-progress drag resizes with no transition at all.
  const wasFullPageRef = useRef(fullPage)
  const wasDockedRef = useRef(false)
  useEffect(() => {
    const root = document.documentElement
    const cameFromFullPage = wasFullPageRef.current
    wasFullPageRef.current = fullPage
    const justOpened = docked && !wasDockedRef.current
    wasDockedRef.current = docked
    if (!docked) {
      root.style.removeProperty("--covi-dock-width")
      root.style.removeProperty("--covi-dock-transition")
      return
    }
    root.style.setProperty("--covi-dock-width", `${width}px`)
    root.style.setProperty(
      "--covi-dock-transition",
      isResizing || cameFromFullPage || justOpened
        ? "none"
        : "margin-right 200ms ease-out",
    )
    return () => {
      root.style.removeProperty("--covi-dock-width")
      root.style.removeProperty("--covi-dock-transition")
    }
  }, [docked, fullPage, width, isResizing])

  return (
    <>
      {onSofiaSurfaceRoute ||
      (isOpen && sofiaPanelAvailable) ||
      shouldKeepBackgroundRuntime ? (
        <CoviPanel
          variant={
            fullPage
              ? "home"
              : onSofiaSurfaceRoute || shouldKeepBackgroundRuntime
                ? "background"
                : isMobile
                  ? "overlay"
                  : "docked"
          }
          counts={counts ?? null}
        />
      ) : null}
    </>
  )
}
